import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import isEqual from 'lodash/isEqual';
import { Checkbox, Input, Label, MultiSelect, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@cargomatic/ui';
import MarketsStore from 'stores/MarketsStore';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import types from '../TariffTypes';
const FALLBACK_MARKETS = ['LAX', 'NYC', 'SFO', 'CHI'];
function getMarketCodes() {
    const list = MarketsStore.getMarkets();
    return (list || [])
        .map((m) => m.code)
        .filter((c) => typeof c === 'string');
}
export function TariffPropsEditor({ tariff, tariffsList, onChange }) {
    const { t } = useTranslation();
    const [marketCodes, setMarketCodes] = useState(() => {
        const initial = getMarketCodes();
        return initial.length ? initial : FALLBACK_MARKETS;
    });
    const [isAllMarketsChecked, setIsAllMarketsChecked] = useState(false);
    useEffect(() => {
        const allMarkets = [...marketCodes].sort();
        const tariffMarkets = [...(tariff.markets || [])].sort();
        setIsAllMarketsChecked(allMarkets.length > 0 && isEqual(allMarkets, tariffMarkets));
    }, [marketCodes, tariff.markets]);
    useEffect(() => {
        const onStoreChange = () => {
            const next = getMarketCodes().sort();
            setMarketCodes(next.length ? next : FALLBACK_MARKETS);
        };
        MarketsStore.addChangeListener(onStoreChange);
        onStoreChange();
        MarketsAPIUtils.getMarkets();
        return () => MarketsStore.removeChangeListener(onStoreChange);
    }, []);
    const selectedMarkets = isAllMarketsChecked
        ? marketCodes
        : tariff.markets || [];
    const disableDefaultTariff = useMemo(() => {
        if (!selectedMarkets || !selectedMarkets.length)
            return false;
        const marketDefaultSet = (tariffsList || []).filter((tar) => {
            var _a;
            const firstMarket = (_a = tar.markets) === null || _a === void 0 ? void 0 : _a[0];
            return (firstMarket === selectedMarkets[0] &&
                tar.is_default &&
                tar._id !== tariff._id);
        });
        if (marketDefaultSet.length && !tariff.is_default)
            return true;
        if (selectedMarkets.length > 1)
            return true;
        return false;
    }, [selectedMarkets, tariffsList, tariff._id, tariff.is_default]);
    const marketOptions = useMemo(() => marketCodes.map((code) => ({ label: code, value: code })), [marketCodes]);
    const selectedMarketOptions = useMemo(() => (selectedMarkets || []).map((m) => ({ label: m, value: m })), [selectedMarkets]);
    const handleMarketsChange = useCallback((next) => {
        onChange('markets', next.map((o) => o.value));
    }, [onChange]);
    const handleAllMarketsToggle = useCallback((checked) => {
        setIsAllMarketsChecked(checked);
        onChange('markets', checked ? marketCodes : []);
    }, [marketCodes, onChange]);
    return (React.createElement("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" },
        React.createElement("div", { className: "space-y-4" },
            React.createElement("div", { className: "space-y-1.5" },
                React.createElement(Label, { htmlFor: "tariff-id" }, t('ID')),
                React.createElement(Input, { id: "tariff-id", value: tariff._id || '', readOnly: true })),
            React.createElement("div", { className: "space-y-1.5" },
                React.createElement(Label, { htmlFor: "tariff-name" }, titleize(t('NAME'))),
                React.createElement(Input, { id: "tariff-name", value: tariff.name || '', onChange: (e) => onChange('name', e.target.value), "aria-invalid": !tariff.name ? true : undefined })),
            React.createElement("div", { className: "space-y-1.5" },
                React.createElement(Label, { htmlFor: "tariff-type" }, t('TYPE')),
                React.createElement(Select, { value: tariff.type || '', onValueChange: (value) => onChange('type', value) },
                    React.createElement(SelectTrigger, { id: "tariff-type" },
                        React.createElement(SelectValue, { placeholder: t('SELECT') })),
                    React.createElement(SelectContent, null, types.map((type) => (React.createElement(SelectItem, { key: type.value, value: type.value }, t(type.localizationSlug)))))))),
        React.createElement("div", { className: "space-y-4" },
            React.createElement("div", { className: "space-y-1.5" },
                React.createElement(Label, null, titleize(t('MARKET', { count: 1 }))),
                React.createElement(MultiSelect, { options: marketOptions, value: selectedMarketOptions, onChange: handleMarketsChange, disabled: isAllMarketsChecked, placeholder: t('SELECT'), searchPlaceholder: t('SEARCH') })),
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement(Checkbox, { id: "tariff-all-markets", checked: isAllMarketsChecked, onCheckedChange: (c) => handleAllMarketsToggle(c === true) }),
                React.createElement(Label, { htmlFor: "tariff-all-markets", className: "font-normal" }, t('ALL_MARKETS'))),
            React.createElement("div", { className: "space-y-1" },
                React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement(Checkbox, { id: "tariff-default", checked: !!tariff.is_default, disabled: disableDefaultTariff, onCheckedChange: (c) => onChange('is_default', c === true) }),
                    React.createElement(Label, { htmlFor: "tariff-default", className: "font-normal" }, disableDefaultTariff
                        ? t('DEFAULT_TARIFF_DISABLED')
                        : t('DEFAULT_TARIFF'))),
                disableDefaultTariff && (React.createElement("p", { className: "text-xs text-muted-foreground" }, t('REMOVE_DEFAULT_TARIFF_MSG')))))));
}
export default TariffPropsEditor;
